/**
 * @file     VowAccessModal.tsx
 * @purpose  Step-up modal that completes the §9.06 VOW registrant requirement
 *           (name + phone + valid email + ToU acknowledgement) at the moment of
 *           intent. Screen machine (account / sign-in / phone / terms /
 *           reactivate / check_email) rendered inside the shared GateSheet.
 *           Phone + ToU are written server-side to user_vow_registrations via
 *           /api/auth/vow-register, never to client-writable user_metadata. On
 *           success, refreshes the server components AND announces the unlock
 *           (useVowUnlock) so client-fetched VOW surfaces refetch too.
 *
 *           Live across the listing surfaces: the map slide-over, the homepage
 *           hero, the listings gate, the off-market wall, and the sold/market
 *           stats panels. Not sandbox-only; treat changes here as production.
 */
'use client';

import { useEffect, useRef, useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { FormConsentLine } from '@/components/lead-capture/FormConsentLine';
import { ConsentChoices, useConsentChoices } from '@/components/lead-capture/ConsentChoices';
import { SocialAuthButtons } from '@/components/gate/SocialAuthButtons';
import {
  GateActions,
  GateNote,
  GatePrimaryButton,
  GateSecondaryButton,
  GateSheet,
  GateTextButton,
  gateColors,
} from '@/components/gate/GateSheet';
import { layoutTokens } from '@/design-system/tokens/layout';
import { VowDataTermsContent } from '@/components/vow/VowDataTermsContent';
import { safeNextPath } from '@/services/auth/emailLinkOrigin';
import { MIN_PASSWORD_LENGTH } from '@/services/auth/passwordRules';
import { useEmailConfirmedWatcher } from '@/hooks/useEmailConfirmedWatcher';
import { clearVowTermsAcceptance, hasVowTermsAcceptance, writeVowTermsAcceptance } from '@/components/vow/vowTermsAcceptance';
import { registerVow, markPasswordSet } from '@/services/auth/accountFlagsClient';
import { announceVowUnlocked } from '@/hooks/useVowUnlock';
import { markVowOAuthIntent } from '@/components/vow/vowOAuthIntent';
import { VOW_PENDING_PHONE_KEY as PENDING_PHONE_KEY, readVowPendingPhone as readPendingPhone } from '@/components/vow/useVowRegistrationResume';
import { vowSignupFlowId, vowSignupLeadType, vowPartialLeadType } from '@/components/vow/vowSignupLeadType';
import { contactPayload } from '@/services/leads/contactFields';
import { formatPhoneInput, toE164 } from '@/utils/formatPhone';
import { AccountPhoneVerify } from '@/components/phone/AccountPhoneVerify';

/* The pending-phone key + reader live in useVowRegistrationResume, which the
 * gated surfaces use to reopen this modal for a returning half-registered
 * user. Parked on the check-email hand-off, cleared after registration. */

function writePendingPhone(value: string | null) {
  // Registration finished (null) ends the parked hand-off: the accepted-terms marker goes with the phone.
  if (!value) clearVowTermsAcceptance();
  try {
    if (value) window.localStorage.setItem(PENDING_PHONE_KEY, value);
    else window.localStorage.removeItem(PENDING_PHONE_KEY);
  } catch {
    /* private mode / storage disabled: the phone step just asks again */
  }
}

type Screen = 'account' | 'signin' | 'phone' | 'terms' | 'reactivate' | 'check_email' | 'confirm_phone';

export function VowAccessModal({
  open,
  onClose,
  authed,
  needsReactivation = false,
  redirectPath,
  eyebrow,
  headline,
  initialFirstName,
  initialLastName,
  initialEmail,
  initialPhone,
  fileLead = true,
}: {
  open: boolean;
  onClose: () => void;
  authed: boolean;
  needsReactivation?: boolean;
  redirectPath?: string;
  eyebrow?: string;
  headline?: string;
  initialFirstName?: string;
  initialLastName?: string;
  /** Details the visitor already gave on this page, so they are not asked twice. */
  initialEmail?: string;
  initialPhone?: string;
  /** False when this page already filed a conversion (save-search). The
   *  account still gets created; Ads must not see a second generate_lead_buyer. */
  fileLead?: boolean;
}) {
  const { t } = useTranslation();
  const { signIn, user } = useAuth();
  const router = useRouter();
  const [screen, setScreen] = useState<Screen>(needsReactivation ? 'reactivate' : authed ? 'phone' : 'account');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const consent = useConsentChoices();
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const [magicState, setMagicState] = useState<'idle' | 'sending' | 'sent'>('idle');
  // Which flow sent the user into the terms step, so "I Agree" runs the right
  // submit. Registrant acceptance (§9.06) IS the explicit click on that button.
  const [termsOrigin, setTermsOrigin] = useState<'account' | 'phone'>('account');
  const [atBottom, setAtBottom] = useState(false);
  const termsScrollRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();
  const formStartTime = useRef(Date.now());
  // Registration reached the point where submitLead already ran, so closing
  // must not also file a partial. Also latches the partial itself so a
  // reopen-and-close loop can't file it twice.
  const leadFiled = useRef(false);

  const phoneValid = toE164(phone) !== null;
  /** First, last, full name, lowercased email and E.164 phone, the shape every lead carries. Consent travels only on the real submit, never the close-path partial. */
  const leadContact = () => contactPayload({ firstName, lastName, email, phone });

  /** The page the visitor is actually reading. This modal opens in place and
   *  never navigates, so lead attribution must read the live location. Using
   *  the post-signup redirect target instead reported a page they never saw. */
  const currentPath = () =>
    typeof window === 'undefined' ? '/' : safeNextPath(window.location.pathname, '/');
  const returnPath = () => safeNextPath(redirectPath ?? currentPath(), '/');

  /** Closing mid-flow: keep whatever they gave us rather than losing the lead.
   *  They have no account and no VOW access, so FUB is told exactly that. */
  const handleClose = () => {
    const trimmedEmail = email.trim();
    if (fileLead && !leadFiled.current && trimmedEmail.includes('@')) {
      leadFiled.current = true;
      void (async () => {
        try {
          const { submitLead } = await import('@/services/leads/submitLead');
          await submitLead(
            {
              type: vowPartialLeadType(currentPath()),
              ...leadContact(),
              // A half-typed number would fail the route's phone check and
              // lose the partial, so only a complete one travels.
              phone: toE164(phone) ?? '',
              sourcePage: currentPath(),
              antiSpam: {
                honeypotFilled: false,
                elapsedSeconds: (Date.now() - formStartTime.current) / 1000,
                submissionTooFast: false,
              },
            },
            { sourcePage: currentPath(), flowId: 'vowPartial' },
          );
        } catch {
          // Non-blocking: never hold the modal open on a recovery attempt.
        }
      })();
    }
    onClose();
  };

  // Pre-fill the parked phone once mounted on the client. Done in an effect,
  // not a lazy useState, so the server and first client render agree.
  useEffect(() => {
    if (!open) return;
    const accountPhone = typeof user?.user_metadata?.phone === 'string' ? formatPhoneInput(user.user_metadata.phone.replace(/^\+1/, '')) : '';
    setPhone((current) => current || initialPhone || readPendingPhone() || accountPhone);
    if (initialFirstName) setFirstName((current) => current || initialFirstName);
    if (initialLastName) setLastName((current) => current || initialLastName);
    if (initialEmail) setEmail((current) => current || initialEmail);
  }, [open, initialFirstName, initialLastName, initialEmail, initialPhone, user?.user_metadata?.phone]);

  // `screen` is seeded once at mount, but `authed` arrives from useAuth, which
  // starts false and resolves async. Where the modal is already mounted when
  // that resolves (the map slide-over keeps it mounted whenever `vowRefused`
  // is set), the seed goes stale and a signed-in registrant is left staring at
  // the create-account form, which then fails with "User already registered".
  // Only the anonymous-only screens are corrected, so an in-progress terms or
  // check_email step is never yanked out from under the user.
  useEffect(() => {
    if (needsReactivation) {
      setScreen('reactivate');
      return;
    }
    if (authed) {
      setScreen((current) => (current === 'account' || current === 'signin' ? 'phone' : current));
    }
  }, [authed, needsReactivation]);

  // When the terms step opens, mark the acknowledgement satisfiable immediately
  // if the copy is short enough that there is nothing to scroll.
  useEffect(() => {
    if (screen !== 'terms') return;
    setAtBottom(false);
    const el = termsScrollRef.current;
    if (el && el.scrollHeight - el.clientHeight < 24) setAtBottom(true);
  }, [screen]);

  const onTermsScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) setAtBottom(true);
  };

  // First click when the user hasn't reached the bottom animates them there;
  // they must click again once at the bottom to actually accept + submit.
  const handleAgree = () => {
    const el = termsScrollRef.current;
    if (el && !atBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      return;
    }
    if (termsOrigin === 'phone') handlePhone();
    else handleAccount();
  };

  const goToTerms = (origin: 'account' | 'phone') => {
    setError('');
    if (!phoneValid) return setError('A valid phone number is required to unlock VOW data.');
    if (origin === 'account' && (!firstName.trim() || !lastName.trim() || !email.trim() || !password)) {
      return setError('Enter your name, email, and password.');
    }
    // Rejected here, not after the terms: the password is checked before anyone reads a screen of terms.
    if (origin === 'account' && password.length < MIN_PASSWORD_LENGTH) {
      return setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    }
    setTermsOrigin(origin);
    // Accepted in this browser before the email round trip: resume without the terms.
    if (origin === 'phone' && hasVowTermsAcceptance()) {
      handlePhone();
      return;
    }
    setScreen('terms');
  };

  const finish = () => {
    onClose();
    // router.refresh() only re-runs Server Components. Every VOW surface that
    // fetched its payload from the client holds a 401/403 answer that nothing
    // else invalidates, so it needs this signal or the blur survives until a
    // hard reload.
    announceVowUnlocked();
    router.refresh();
  };

  async function loadSupabase() {
    // Shared client, NOT a raw createBrowserClient: the shared client uses
    // flowType 'implicit', so confirmation links keep working when the email
    // opens in a different browser than the one that signed up (mobile mail
    // apps, scanner prefetch). Default PKCE dead-ends those users unconfirmed.
    const { getSupabaseBrowserClient } = await import('@/core/supabase/client');
    const client = await getSupabaseBrowserClient();
    if (!client) throw new Error('Supabase unavailable');
    return client;
  }

  function handleAccount() {
    setError('');
    if (!phoneValid) return setError('A valid phone number is required to unlock VOW data.');
    // The click on "I agree" brought us here; remember it in case email confirmation interrupts.
    writeVowTermsAcceptance();
    startTransition(async () => {
      try {
        const supabase = await loadSupabase();
        // Account creation only; VOW registrant access is written server-side
        // (never in client-writable user_metadata).
        const { data: signUpData, error: err } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { first_name: firstName.trim(), last_name: lastName.trim() },
            // Land on /auth/finish so the session in the URL hash is actually
            // persisted to cookies before returning to the gated page. Pointing
            // the link straight at the gated page consumed the token without
            // signing the user in.
            emailRedirectTo: `${window.location.origin}/auth/finish?next=${encodeURIComponent(returnPath())}`,
          },
        });
        if (err) return setError(err.message);
        // Supabase obfuscates duplicate emails as a success with an empty
        // identities array. Route those users to sign-in instead of promising
        // a confirmation email that will never arrive.
        if (signUpData.user?.identities?.length === 0) {
          setScreen('signin');
          return setError('You already have an account with this email. Sign in instead.');
        }
        // Enter the lead pipeline (FUB contact + new-signup notification +
        // profile provisioning). Raw signUp alone leaves the account invisible.
        // Signup succeeded, so the full lead below supersedes any partial.
        // On /property-finder this is a buyer conversion (generate_lead_buyer),
        // matching the listings gate. Other VOW surfaces stay user-signup.
        leadFiled.current = true;
        if (fileLead) {
          try {
            const { submitLead } = await import('@/services/leads/submitLead');
            const type = vowSignupLeadType(currentPath());
            await submitLead(
              {
                type,
                ...leadContact(),
                ...consent.payload(phone),
                sourcePage: currentPath(),
                antiSpam: {
                  honeypotFilled: false,
                  elapsedSeconds: (Date.now() - formStartTime.current) / 1000,
                  submissionTooFast: false,
                },
              },
              { sourcePage: currentPath(), flowId: vowSignupFlowId(type) },
            );
          } catch {
            // Non-blocking
          }
        }
        void markPasswordSet('vow-access-modal');
        // Register VOW server-side only when signUp established a session. If
        // email confirmation is pending (no session), the user completes
        // registration via the phone screen once authenticated.
        const { data: sess } = await supabase.auth.getSession();
        if (sess.session) {
          const reg = await registerVow({ mode: 'activate', phone });
          if (!reg.ok && reg.error) return setError(reg.error);
          writePendingPhone(null);
          confirmPhoneOrFinish(reg.phoneVerified);
        } else {
          // Email confirmation is required, so there is no session yet and VOW
          // cannot be registered server-side. Tell the user clearly instead of
          // closing as if access were granted; they finish the phone step after
          // confirming their email. Park the phone so that step is pre-filled.
          writePendingPhone(phone);
          setScreen('check_email');
        }
      } catch {
        setError('Something went wrong. Please try again.');
      }
    });
  }

  function handlePhone() {
    setError('');
    if (!phoneValid) return setError('A valid phone number is required to unlock VOW data.');
    startTransition(async () => {
      const reg = await registerVow({ mode: 'activate', phone });
      if (!reg.ok) return setError(reg.error ?? 'Something went wrong. Please try again.');
      writePendingPhone(null);
      confirmPhoneOrFinish(reg.phoneVerified);
    });
  }

  /** Access is already granted server-side; the code proves the number the
   *  consent text promised to text. Skipping or a send failure still lets
   *  them in. */
  function confirmPhoneOrFinish(phoneVerified: boolean | undefined) {
    if (phoneVerified === false && toE164(phone)) setScreen('confirm_phone');
    else finish();
  }

  /** The confirmation mail is the only way out of `check_email`, so a message
   *  that never lands is a dead end. Let them ask for another one. */
  function handleResend() {
    setError('');
    setResendState('sending');
    startTransition(async () => {
      try {
        const supabase = await loadSupabase();
        const { error: err } = await supabase.auth.resend({
          type: 'signup',
          email,
          options: { emailRedirectTo: `${window.location.origin}/auth/finish?next=${encodeURIComponent(returnPath())}` },
        });
        if (err) {
          setResendState('idle');
          return setError(err.message);
        }
        setResendState('sent');
      } catch {
        setResendState('idle');
        setError('Could not resend the email. Please try again.');
      }
    });
  }

  function handleReactivate() {
    setError('');
    startTransition(async () => {
      const reg = await registerVow({ mode: 'reactivate' });
      if (!reg.ok) return setError(reg.error ?? 'Something went wrong. Please try again.');
      finish();
    });
  }

  function handleSignIn() {
    setError('');
    startTransition(async () => {
      const res = await signIn(email, password);
      if (res.error) return setError(res.error);
      // Signed in. They may still need a phone; re-evaluate on the server.
      finish();
    });
  }

  /** Accounts created by the listings gate or lead capture are passwordless,
   *  so the password field is a dead end for them. The emailed link is the
   *  only sign-in those users have. */
  function handleMagicLink() {
    setError('');
    const trimmed = email.trim();
    if (!trimmed.includes('@')) return setError('Enter your email above to get a link.');
    setMagicState('sending');
    startTransition(async () => {
      try {
        const { secureFetch } = await import('@/services/anti-scraping/secureFetch');
        const res = await secureFetch('/api/auth/resend-magic-link', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email: trimmed, next: returnPath() }),
        });
        if (res.status === 429) {
          setMagicState('idle');
          return setError('Try again in a minute.');
        }
        setMagicState('sent');
      } catch {
        setMagicState('idle');
        setError('Could not send the link. Please try again.');
      }
    });
  }

  // The confirmation link is usually opened in another tab or app. When the
  // session shows up, finish the registration if the terms were already
  // accepted, otherwise move to the phone step; Done stays as the fallback.
  useEmailConfirmedWatcher(open && screen === 'check_email', () => {
    if (phoneValid && hasVowTermsAcceptance()) handlePhone();
    else setScreen('phone');
  });

  const field = {
    fullWidth: true,
    variant: 'outlined' as const,
    sx: { '& .MuiOutlinedInput-root': { borderRadius: `${layoutTokens.radius.md}px`, fontSize: 16 } },
  };
  // On a phone the sheet fills the screen and scrolls, so the keyboard can
  // never hide the button. No autofocus there: opening straight into the
  // keyboard covered the form and made "Continue" unreachable.
  const isPhone = useMediaQuery('(max-width: 599.95px)');
  /** `autoFocus` only on the phone-only screen: on the account screen the first
   *  name field owns focus, and two autoFocus fields fight over it. */
  const phoneField = (autoFocus: boolean) => (
    <Box>
      <TextField
        {...field}
        label={t('vowGate.phoneLabel', { defaultValue: 'Mobile phone' })}
        type="tel"
        autoComplete="tel"
        value={phone}
        onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
        autoFocus={autoFocus && !isPhone}
      />
    </Box>
  );

  const screenRank: Record<Screen, number> = { account: 0, signin: 0, phone: 0, reactivate: 0, terms: 1, check_email: 2, confirm_phone: 2 };
  const prevScreen = useRef<Screen>(screen);
  const stepDirection = screenRank[screen] < screenRank[prevScreen.current] ? 'back' : 'forward';
  useEffect(() => {
    prevScreen.current = screen;
  }, [screen]);

  const title =
    screen === 'reactivate'
      ? t('vowGate.reactivateTitle', { defaultValue: 'Turn your access back on' })
      : screen === 'terms'
        ? t('vowGate.termsTitle', { defaultValue: 'VOW Data Terms of Use' })
        : screen === 'phone'
          ? t('vowGate.phoneTitle', { defaultValue: 'Add your phone to see the data' })
          : screen === 'signin'
            ? t('vowGate.signinTitle', { defaultValue: 'Welcome back' })
            : screen === 'check_email'
              ? t('vowGate.checkEmailTitle', { defaultValue: 'Confirm your email' })
              : screen === 'confirm_phone'
                ? t('vowGate.confirmPhoneTitle', { defaultValue: 'Confirm your phone' })
                : (headline ?? t('vowGate.headline', { defaultValue: 'See price history and sold data' }));

  const steps =
    screen === 'account'
      ? { current: 1, total: 2, label: t('vowGate.stepDetails', { defaultValue: 'Your details' }) }
      : screen === 'phone'
        ? { current: 1, total: 2, label: t('vowGate.stepPhone', { defaultValue: 'Your phone' }) }
        : screen === 'terms'
          ? { current: 2, total: 2, label: t('vowGate.stepTerms', { defaultValue: 'Board terms' }) }
          : undefined;

  const statusLabel =
    screen === 'signin'
      ? t('vowGate.statusSignin', { defaultValue: 'Sign in' })
      : screen === 'reactivate'
        ? t('vowGate.statusReturning', { defaultValue: 'Returning member' })
        : screen === 'check_email'
          ? t('vowGate.statusCheckEmail', { defaultValue: 'Almost done' })
          : undefined;

  return (
    <GateSheet
      open={open}
      onClose={handleClose}
      eyebrow={eyebrow ?? t('vowGate.eyebrow', { defaultValue: 'Member data' })}
      title={title}
      steps={steps}
      statusLabel={statusLabel}
      stepKey={screen}
      stepDirection={stepDirection}
      fillOnPhone
      testId="vow-access-gate"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {screen === 'account' && (
        <Stack spacing={3}>
          <SocialAuthButtons disabled={pending} onBeforeRedirect={markVowOAuthIntent} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <TextField {...field} label={t('vowGate.firstName', { defaultValue: 'First name' })} autoComplete="given-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus={!isPhone} />
            <TextField {...field} label={t('vowGate.lastName', { defaultValue: 'Last name' })} autoComplete="family-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Stack>
          <TextField {...field} label={t('vowGate.email', { defaultValue: 'Email' })} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField {...field} label={t('vowGate.password', { defaultValue: 'Password' })} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {phoneField(false)}
          <ConsentChoices phone values={consent.values} onChange={consent.setValues} disabled={pending} />
          <GatePrimaryButton pending={pending} onClick={() => goToTerms('account')}>
            {t('vowGate.continue', { defaultValue: 'Continue' })}
          </GatePrimaryButton>
          <FormConsentLine />
          <GateNote>
            {t('vowGate.haveAccount', { defaultValue: 'Already have an account?' })}{' '}
            <GateTextButton onClick={() => setScreen('signin')}>
              {t('vowGate.signIn', { defaultValue: 'Sign in' })}
            </GateTextButton>
          </GateNote>
        </Stack>
      )}

      {screen === 'signin' && (
        <Stack spacing={3}>
          <SocialAuthButtons disabled={pending} onBeforeRedirect={markVowOAuthIntent} />
          <TextField {...field} label={t('vowGate.email', { defaultValue: 'Email' })} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus={!isPhone} />
          <TextField {...field} label={t('vowGate.password', { defaultValue: 'Password' })} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <GatePrimaryButton pending={pending} onClick={handleSignIn}>
            {t('vowGate.signIn', { defaultValue: 'Sign in' })}
          </GatePrimaryButton>
          <GateNote>
            {magicState === 'sent' ? (
              <Box component="span" role="status">
                {t('vowGate.magicSent', { defaultValue: 'Link sent. Check your email and click it to sign in.' })}
              </Box>
            ) : (
              <>
                {t('vowGate.noPassword', { defaultValue: 'No password or forgot it?' })}{' '}
                <GateTextButton onClick={() => magicState === 'idle' && handleMagicLink()} disabled={magicState === 'sending'}>
                  {magicState === 'sending'
                    ? t('vowGate.sending', { defaultValue: 'Sending...' })
                    : t('vowGate.magicLink', { defaultValue: 'Send a sign-in link' })}
                </GateTextButton>
              </>
            )}
          </GateNote>
          <GateNote>
            {t('vowGate.newHere', { defaultValue: 'New here?' })}{' '}
            <GateTextButton onClick={() => setScreen('account')}>
              {t('vowGate.createAccount', { defaultValue: 'Create an account' })}
            </GateTextButton>
          </GateNote>
        </Stack>
      )}

      {screen === 'phone' && (
        <Stack spacing={4}>
          {phoneField(true)}
          <GatePrimaryButton pending={pending} onClick={() => goToTerms('phone')}>
            {t('vowGate.continue', { defaultValue: 'Continue' })}
          </GatePrimaryButton>
          <FormConsentLine />
        </Stack>
      )}

      {screen === 'confirm_phone' && toE164(phone) && (
        <AccountPhoneVerify phone={toE164(phone) as string} onVerified={finish} onSkip={finish} />
      )}

      {screen === 'check_email' && (
        <Stack spacing={4}>
          <Typography sx={{ fontSize: 16, color: gateColors.text, lineHeight: 1.55 }}>
            {t('vowGate.checkEmailBody', {
              defaultValue:
                'Account created. Confirm your email, then come back to this page.',
            })}
          </Typography>
          <GatePrimaryButton pending={pending} onClick={onClose}>
            {t('vowGate.done', { defaultValue: 'Done' })}
          </GatePrimaryButton>
          <GateNote>
            {resendState === 'sent' ? (
              <Box component="span" role="status">
                {t('vowGate.resent', { defaultValue: 'Sent. Check your spam folder too.' })}
              </Box>
            ) : (
              <>
                {t('vowGate.didNotGet', { defaultValue: 'Did not get it?' })}{' '}
                <GateTextButton onClick={() => resendState === 'idle' && handleResend()} disabled={resendState === 'sending'}>
                  {resendState === 'sending'
                    ? t('vowGate.sending', { defaultValue: 'Sending...' })
                    : t('vowGate.resend', { defaultValue: 'Resend the email' })}
                </GateTextButton>
              </>
            )}
          </GateNote>
        </Stack>
      )}

      {screen === 'reactivate' && (
        <Stack spacing={4}>
          <Typography sx={{ fontSize: 15, color: gateColors.textSecondary, lineHeight: 1.55 }}>
            {t('vowGate.reactivateBody', {
              defaultValue:
                'Board rules pause access to sold prices and price history every 30 days. Turn it back on below.',
            })}
          </Typography>
          {user?.email && (
            <Box sx={{ px: 3.5, py: 3, borderRadius: '8px', backgroundColor: gateColors.surfaceSunken, fontSize: 14, color: gateColors.textSecondary, overflowWrap: 'anywhere' }}>
              {t('vowGate.signedInAs', { defaultValue: 'Signed in as' })} {user.email}
            </Box>
          )}
          <GatePrimaryButton pending={pending} onClick={handleReactivate}>
            {t('vowGate.reactivate', { defaultValue: 'Turn access back on' })}
          </GatePrimaryButton>
          <GateNote>
            <Box component={Link} href="/legal/vow-data-terms" target="_blank" rel="noopener" sx={{ color: gateColors.link, fontWeight: 500 }}>
              {t('vowGate.readTermsAgain', { defaultValue: 'Read the terms again' })}
            </Box>
          </GateNote>
        </Stack>
      )}

      {screen === 'terms' && (
        <Stack spacing={3}>
          <Typography sx={{ fontSize: 14, color: gateColors.textSecondary, lineHeight: 1.5 }}>
            {t('vowGate.termsLead', { defaultValue: 'Read the Terms of Use below. Select I agree to see VOW data.' })}
          </Typography>
          <Box
            ref={termsScrollRef}
            onScroll={onTermsScroll}
            role="region"
            aria-label={t('vowGate.termsRegion', { defaultValue: 'VOW terms of use' })}
            tabIndex={0}
            data-testid="vow-terms-scroll"
            sx={{
              // Capped so the terms stay usable when the mobile keyboard
              // shrinks the viewport.
              maxHeight: 'min(260px, 40vh)',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
              px: 4,
              py: 4,
              borderRadius: '8px',
              border: `1px solid ${gateColors.border}`,
              backgroundColor: gateColors.surfaceSunken,
              '& p': { fontSize: 13.5, lineHeight: 1.6 },
              '& p:last-child': { mb: 0 },
              '&:focus-visible': { outline: `2px solid ${gateColors.focus}`, outlineOffset: 2 },
            }}
          >
            <VowDataTermsContent />
          </Box>
          <Typography aria-live="polite" sx={{ fontSize: 13, color: atBottom ? gateColors.muted : gateColors.link, lineHeight: 1.4 }}>
            {atBottom
              ? t('vowGate.termsHintDone', { defaultValue: 'Selecting I agree records your acceptance.' })
              : t('vowGate.termsHintScroll', { defaultValue: 'Scroll to the end of the terms to continue.' })}
          </Typography>
          <GateActions>
            <GateSecondaryButton onClick={() => setScreen(termsOrigin === 'phone' ? 'phone' : 'account')}>
              {t('gate.sheet.back', { defaultValue: 'Back' })}
            </GateSecondaryButton>
            <GatePrimaryButton pending={pending} onClick={handleAgree}>
              {t('vowGate.agree', { defaultValue: 'I agree' })}
            </GatePrimaryButton>
          </GateActions>
        </Stack>
      )}
    </GateSheet>
  );
}
